import React from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faAward,
  faHandshake,
  faSeedling,
  faLightbulb,
} from "@fortawesome/free-solid-svg-icons";
import LOGO from "../assets/logo-BG.jpg";
import API_CLIENT from "../lib/api-client";
import { GET_ALL_MEMBERS } from "../utils/constant";
import { useState, useEffect } from "react";
import Loading from "../component/loading";
import Skeleton, { ProfileSkeleton, TeamMemberSkeleton } from '../component/Skeleton';

const AboutUs = () => {
  const [teamMembers, setTeamMembers] = useState();
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchTeamMembers = async () => {
      try {
        setLoading(true)
        const response = await API_CLIENT.get(GET_ALL_MEMBERS);
        if (response.status === 200) {
          console.log(response);
          
          setTeamMembers(response.data.teamMembers);
          setLoading(false);
        }
      } catch (error) {
        setLoading(false);
      }
    };
    fetchTeamMembers();
  }, []);

  const values = [
    {
      name: "Quality",
      description:
        "We are committed to delivering the highest quality fabrics to our customers.",
      icon: faAward,
    },
    {
      name: "Sustainability",
      description:
        "We prioritize sustainable practices and materials throughout our supply chain.",
      icon: faSeedling,
    },
    {
      name: "Innovation",
      description:
        "We constantly push boundaries to create cutting-edge designs and technologies.",
      icon: faLightbulb,
    },
    {
      name: "Customer Focus",
      description:
        "We put our customers at the heart of everything we do, providing exceptional service and support.",
      icon: faHandshake,
    },
  ];

  return (
    <div className="bg-neutral-100 font-sans text-neutral-800">
      {/* Hero Section */}
      <section className="relative bg-white py-20">
        <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1620799140408-edc6dcb6d633?ixlib=rb-4.0.3')] bg-cover bg-center opacity-5"></div>

        <div className="container mx-auto px-6 relative z-10">
          <div className="max-w-4xl mx-auto">
            <p className="text-teal-600 font-medium tracking-widest uppercase mb-6 text-sm">
              About Our Company
            </p>
            <h1 className="hero-title text-5xl md:text-7xl font-light leading-tight mb-8 text-neutral-900">
              Weaving Dreams into Reality
            </h1>
            <p className="hero-text text-xl md:text-2xl text-neutral-600 max-w-3xl leading-relaxed">
              Since 2010, we've been crafting premium fabrics that combine
              traditional craftsmanship with innovative design, creating
              textiles that transform spaces and fashion.
            </p>
            <div className="mt-12 flex flex-wrap gap-4">
              <a
                href="#story"
                className="px-8 py-3 bg-teal-600 text-white rounded-sm transition hover:bg-teal-700"
              >
                Our Story
              </a>
              <a
                href="#contact"
                className="px-8 py-3 border border-neutral-300 hover:border-neutral-400 rounded-sm transition"
              >
                Get in Touch
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Story Section */}
      <section id="story" className="py-20 bg-neutral-100">
        <div className="container mx-auto px-6">
          <div className="max-w-3xl mx-auto mb-16 text-center">
            <h2 className="text-3xl md:text-4xl font-light mb-6 text-neutral-900">
              Our Journey
            </h2>
            <div className="w-16 h-1 bg-teal-500 mx-auto"></div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-12 gap-12 items-center">
            <div className="md:col-span-5">
              <div className="relative">
                <div className="absolute inset-0 border border-neutral-600 translate-x-4 translate-y-4"></div>
                <img
                  src={LOGO}
                  alt="Fabric manufacture"
                  className="w-full h-auto object-cover relative z-10"
                />
              </div>
            </div>

            <div className="md:col-span-7">
              <div className="space-y-8">
                <div className="story-content">
                  <span className="inline-block mb-2 text-teal-600 font-medium">
                    2010
                  </span>
                  <h3 className="text-2xl mb-3 font-light text-neutral-900">
                    A Vision Takes Shape
                  </h3>
                  <p className="text-neutral-600">
                    FabricFlow was founded with a vision to revolutionize the
                    textile industry. What started as a small family business
                    has grown into a global enterprise, serving customers in
                    over 50 countries.
                  </p>
                </div>

                <div className="story-content">
                  <span className="inline-block mb-2 text-teal-600 font-medium">
                    2015
                  </span>
                  <h3 className="text-2xl mb-3 font-light text-neutral-900">
                    Growth & Innovation
                  </h3>
                  <p className="text-neutral-600">
                    Our journey has been one of continuous learning, innovation,
                    and growth. We have invested heavily in state-of-the-art
                    technology and sustainable practices to ensure that our
                    fabrics are not only of the highest quality but also
                    environmentally friendly.
                  </p>
                </div>

                <div className="story-content">
                  <span className="inline-block mb-2 text-teal-600 font-medium">
                    Today
                  </span>
                  <h3 className="text-2xl mb-3 font-light text-neutral-900">
                    Global Recognition
                  </h3>
                  <p className="text-neutral-600">
                    Today, FabricFlow is a trusted partner for leading fashion
                    brands, interior designers, and manufacturers worldwide. Our
                    fabrics grace the runways of fashion capitals, adorn the
                    interiors of luxury hotels, and provide comfort in homes
                    across the globe.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-6">
          <div className="max-w-3xl mx-auto mb-16 text-center">
            <h2 className="text-3xl md:text-4xl font-light mb-6 text-neutral-900">
              Our Core Values
            </h2>
            <div className="w-16 h-1 bg-teal-500 mx-auto mb-6"></div>
            <p className="text-neutral-600">
              The principles that guide every thread we weave and every fabric
              we create.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value, index) => (
              <div
                key={index}
                className="group p-8 border border-neutral-200 hover:border-teal-300 transition-all duration-300 hover:shadow-lg bg-white"
              >
                <div className="w-12 h-12 mb-6 flex items-center justify-center rounded-full bg-teal-50 text-teal-600 group-hover:bg-teal-600 group-hover:text-white transition-colors duration-300">
                  <FontAwesomeIcon icon={value.icon} className="text-lg" />
                </div>
                <h3 className="text-xl mb-4 font-light text-neutral-900 group-hover:text-teal-600 transition-colors duration-300">
                  {value.name}
                </h3>
                <p className="text-neutral-600">{value.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Team Section */}
      {/* <section className="py-20 bg-neutral-100">
        <div className="container mx-auto px-6">
          <div className="max-w-3xl mx-auto mb-16 text-center">
            <h2 className="text-3xl md:text-4xl font-light mb-6 text-neutral-900">
              Meet Our Team
            </h2>
            <div className="w-16 h-1 bg-teal-500 mx-auto mb-6"></div>
            <p className="text-neutral-600">
              The creative minds and skilled hands behind our exquisite fabrics.
            </p>
          </div>
          
          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {[...Array(3)].map((_, index) => (
                <TeamMemberSkeleton key={index} />
              ))}
            </div>
          ) : (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                {teamMembers.map((member, index) => (
                  <div
                    key={index}
                    className="bg-white p-8 border border-neutral-200 hover:border-teal-300 transition-all duration-300 hover:shadow-lg"
                  >
                    <div className="mb-6 aspect-square w-full overflow-hidden">
                      <img
                        className="w-full h-full object-cover transition-transform duration-700 hover:scale-105"
                        src={member.image}
                        alt={member.name}
                      />
                    </div>
                    <h3 className="text-xl font-medium text-neutral-900">
                      {member.name}
                    </h3>
                    <p className="text-teal-600 mb-4">{member.role}</p>
                    <p className="text-neutral-600">{member.details}</p>
                  </div>
                ))}
              </div>

              <div className="mt-12 text-center">
                <a
                  href="#contact"
                  className="inline-block px-8 py-3 border border-neutral-300 hover:border-teal-500 hover:text-teal-600 rounded-sm transition"
                >
                  Join Our Team
                </a>
              </div>
            </>
          )}
        </div>
      </section> */}

      {/* Contact CTA Section */}
      <section id="contact" className="py-20 bg-teal-700 text-white">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl md:text-5xl font-light mb-8">
              Let's Create Something Beautiful Together
            </h2>
            <p className="text-teal-100 text-lg mb-12 max-w-2xl mx-auto">
              Whether you're looking for custom fabrics for your fashion
              collection, or premium textiles for interior design, we're here to
              help bring your vision to life.
            </p>
            <a
              href="/contact"
              className="inline-block px-10 py-4 bg-white text-teal-700 hover:bg-teal-50 rounded-sm transition-colors duration-300 text-lg"
            >
              Contact Us Today
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AboutUs;
